import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Info, Loader2 } from "lucide-react";

import {
  FiscalProfileEditor,
  type FiscalProfileEditorRef,
} from "@/components/features/fiscal-profile/FiscalProfileEditor";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  fiscalProfileEditorValuesFromSources,
  toExoneratedRegimePayload,
  toUpdateFiscalProfilePayload,
} from "@/lib/fiscalProfileEditorMappers";
import {
  getExoneratedRegimeForTenant,
  getFiscalProfileForTenant,
  updateExoneratedRegimeForTenant,
  updateFiscalProfileForTenant,
} from "@/services/fiscalService";

/**
 * Accountant-facing editor for a client's DGI fiscal profile, rendered in the
 * "Perfil fiscal" tab of the tenant detail page. Reuses the same
 * FiscalProfileEditor + mappers the SMB settings card uses, but reads/writes
 * via the membership-gated accountant endpoints (cross-tenant).
 *
 * The exonerated-regime fields (checkbox + leyendas) live on the company row,
 * not on fiscal_profile, so they ride a second query/mutation pair. Both writes
 * have to land before we claim success — the section used to render here while
 * silently discarding every edit.
 */
export function AccountantFiscalProfileCard({
  tenantId,
}: {
  tenantId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const editorRef = useRef<FiscalProfileEditorRef>(null);
  const [saving, setSaving] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["accountant", "tenant-fiscal-profile", tenantId],
    queryFn: () => getFiscalProfileForTenant(tenantId),
    retry: false,
  });

  const { data: exonerated, isLoading: isLoadingExonerated } = useQuery({
    queryKey: ["accountant", "tenant-exonerated-regime", tenantId],
    queryFn: () => getExoneratedRegimeForTenant(tenantId),
    retry: false,
  });

  // The leyenda dropdown's option list is derived from the client's industry,
  // so hydrating the form and building the save payload must both use the
  // tenant's real industry — passing null here would round-trip a
  // healthcare/education client's stored legend through the wrong option list
  // and silently degrade it to "Otro (texto personalizado)".
  const industry = exonerated?.industry ?? null;

  const initialValues = useMemo(
    () => fiscalProfileEditorValuesFromSources(profile ?? null, exonerated ?? null),
    [profile, exonerated],
  );
  const resetToken = useMemo(
    () =>
      `${profile?.id ?? "np"}-${profile?.updatedAt ?? ""}-${
        exonerated?.exoneratedRegimeEnabled ?? "ne"
      }-${exonerated?.exoneratedRegimeLegend ?? ""}`,
    [
      profile?.id,
      profile?.updatedAt,
      exonerated?.exoneratedRegimeEnabled,
      exonerated?.exoneratedRegimeLegend,
    ],
  );

  const handleSubmit = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor?.validate() || !profile) return;
    const values = editor.getValues();
    setSaving(true);

    // The profile and the exoneration branding are two endpoints on two
    // tables, so there is no way to make them atomic. What we can do is not
    // lie about the outcome: keep the writes in separate try blocks so a
    // half-save reports as a half-save, and re-hydrate from the server on
    // partial failure so the form stops showing input that was discarded.
    const refresh = async (): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-fiscal-profile", tenantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-exonerated-regime", tenantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-detail", tenantId],
      });
    };

    try {
      try {
        await updateFiscalProfileForTenant(
          tenantId,
          toUpdateFiscalProfilePayload(values, {
            hideAgendaFields: false,
            taxesWhenHidden: profile.taxes.slice(),
            bpsWhenHidden: profile.bpsCompanyNumber ?? null,
          }),
        );
      } catch {
        // Nothing was written — the generic message is accurate here.
        toast.error(
          t(
            "accountantFiscalProfile.error",
            "No pudimos guardar el perfil fiscal.",
          ),
        );
        return;
      }

      try {
        await updateExoneratedRegimeForTenant(
          tenantId,
          toExoneratedRegimePayload(values, industry),
        );
      } catch {
        // The profile half is already committed. Say so, and resync the form
        // so the leyenda fields show what is actually stored.
        await refresh();
        toast.error(
          t(
            "accountantFiscalProfile.exonerationError",
            "Guardamos el perfil fiscal, pero no pudimos guardar la leyenda de régimen exonerado. Volvé a intentarlo.",
          ),
        );
        return;
      }

      await refresh();
      toast.success(
        t("accountantFiscalProfile.success", "Perfil fiscal actualizado."),
      );
    } finally {
      setSaving(false);
    }
  }, [tenantId, profile, industry, queryClient, t]);

  if (isLoading || isLoadingExonerated) {
    return (
      <Card>
        <CardContent className="flex min-h-[200px] items-center justify-center py-8">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t("accountantFiscalProfile.loading", "Cargando…")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-foreground">
          {t("accountantFiscalProfile.title", "Perfil fiscal")}
        </CardTitle>
        <CardDescription>
          {t(
            "accountantFiscalProfile.description",
            "Datos fiscales del cliente. Editá y guardá los cambios.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {profile ? (
          <FiscalProfileEditor
            ref={editorRef}
            idPrefix="accountant-fiscal"
            initialValues={initialValues}
            resetToken={resetToken}
            hideAgendaFields={false}
            industryForLegends={industry}
            showSubmitButton
            isSaving={saving}
            onSubmit={handleSubmit}
          />
        ) : (
          <div
            role="status"
            className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200"
          >
            <Info className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
            <span>
              {t(
                "accountantFiscalProfile.noProfile",
                "Este cliente todavía no tiene un perfil fiscal configurado. Accedé como cliente para crearlo desde el onboarding.",
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
